// ============================================================================
// HOOPS FOR THOUGHT: SEASON ADVANCE ENGINE
// Rolls contracts forward, handles options, clears dead cap, resets season
// ============================================================================

import { generateSchedule } from './scheduleGenerator';

/**
 * Advance all contracts by one year.
 * salary2627 → salary2526, salary2728 → salary2627, salary2829 → salary2728
 * Handles team options (decline → remove), player options (accept → keep)
 * Clears expired dead cap entries
 * Returns new salaryData object
 */
export function advanceContracts(salaryData) {
  const sd = JSON.parse(JSON.stringify(salaryData));

  for (const [abbr, team] of Object.entries(sd)) {
    if (!team?.players) continue;

    const keptPlayers = [];

    for (const p of team.players) {
      // Dead cap entry — roll forward
      if (p.isDead) {
        const newEntry = {
          ...p,
          salary2526: p.salary2627 || 0,
          salary2627: p.salary2728 || 0,
          salary2728: p.salary2829 || 0,
          salary2829: 0,
        };
        // If no more dead money, drop the entry
        if (newEntry.salary2526 > 0 || newEntry.salary2627 > 0 || newEntry.salary2728 > 0) {
          keptPlayers.push(newEntry);
        }
        continue;
      }

      // No future salary at all — contract expired, player becomes FA
      if (!p.salary2627 && !p.salary2728 && !p.salary2829) {
        continue; // Remove from roster (already added to FA pool in offseason flow)
      }

      // Parse option for the upcoming year
      const optionInfo = parseOption(p.option, '26-27');

      if (optionInfo === 'team-option') {
        // AI declines team options on bad contracts, accepts on good ones
        // Simple heuristic: if salary > 15M, decline (bad value assumed)
        if (p.salary2627 > 15000000) {
          continue; // Declined — player removed
        }
        // Accept — keep player
      } else if (optionInfo === 'qualifying-offer') {
        // QO: team extends QO, player tests market (simplified: just let them expire)
        continue;
      }
      // Player options: player always accepts (guaranteed money)

      // Roll forward
      keptPlayers.push({
        ...p,
        salary2526: p.salary2627 || 0,
        salary2627: p.salary2728 || 0,
        salary2728: p.salary2829 || 0,
        salary2829: 0,
        // Clear option string that was for the expired year
        option: advanceOptionString(p.option),
      });
    }

    sd[abbr].players = keptPlayers;
    sd[abbr].payroll = keptPlayers.reduce((s, p) => s + (p.salary2526 || 0), 0);
  }

  return sd;
}

/**
 * Parse option string to check if a specific year has a team option, player option, or QO
 * option formats: "T 26-27", "P 27-28", "T 27-28, Q 28-29"
 */
function parseOption(optStr, yearStr) {
  if (!optStr) return null;
  // yearStr like "26-27"
  const parts = optStr.split(',').map(s => s.trim());
  for (const part of parts) {
    if (part.includes(yearStr) || part.includes('20' + yearStr.split('-')[0])) {
      if (part.startsWith('T')) return 'team-option';
      if (part.startsWith('P')) return 'player-option';
      if (part.startsWith('Q')) return 'qualifying-offer';
    }
  }
  return null;
}

/**
 * Remove option markers for expired years, keep future ones
 */
function advanceOptionString(optStr) {
  if (!optStr) return '';
  const parts = optStr.split(',').map(s => s.trim());
  // Keep only options for 27-28 and beyond (which become the new "next year")
  const kept = parts.filter(part => {
    // Remove 25-26 and 26-27 options (they're now past)
    if (part.includes('25-26') || part.includes('26-27') || part.includes('2025') || part.includes('2026')) {
      return false;
    }
    return true;
  });
  return kept.join(', ');
}

/**
 * Generate lottery order from standings (inverse: worst team = pick 1)
 * Returns array of { pick, team } in draft order
 */
export function generateLotteryOrder(standings) {
  if (!standings) return [];

  // Combine east/west, sort by worst record
  const allTeams = [...(standings.east || []), ...(standings.west || [])];
  allTeams.sort((a, b) => {
    const pctA = a.wins / (a.wins + a.losses || 1);
    const pctB = b.wins / (b.wins + b.losses || 1);
    return pctA - pctB; // Worst first
  });

  // Top 14 = lottery teams, rest by record
  return allTeams.map((t, i) => ({
    pick: i + 1,
    team: t.abbr,
    record: `${t.wins}-${t.losses}`,
  }));
}

/**
 * Get rookie scale salary based on pick number (2026 values, approximate)
 * Source: CBA rookie scale
 */
export function getRookieScaleSalary(pickNumber) {
  // Approximate 2026 rookie scale (1st year salary)
  const scale = [
    13200000, 11800000, 10600000, 9600000, 8700000,  // 1-5
    8000000, 7300000, 6700000, 6100000, 5600000,      // 6-10
    5200000, 4800000, 4400000, 4100000, 3800000,      // 11-15
    3500000, 3300000, 3100000, 2900000, 2700000,      // 16-20
    2500000, 2400000, 2300000, 2200000, 2100000,      // 21-25
    2050000, 2000000, 1950000, 1930000, 1910000,      // 26-30
  ];

  if (pickNumber <= 0 || pickNumber > 60) return 1200000; // Minimum
  if (pickNumber <= 30) return scale[pickNumber - 1];

  // 2nd round: 2-way or minimum
  return 1200000;
}

/**
 * Build a rookie contract entry for salaryData
 */
export function buildRookieContract(prospectName, pickNumber, ovr) {
  const baseSalary = getRookieScaleSalary(pickNumber);
  const isFirstRound = pickNumber <= 30;

  return {
    name: prospectName,
    nbaComId: 0,
    salary2526: 0,  // Drafted for next season
    salary2627: baseSalary,
    salary2728: Math.round(baseSalary * 1.05), // ~5% raise
    salary2829: isFirstRound ? Math.round(baseSalary * 1.10) : 0, // 3rd year for 1st rounders
    option: isFirstRound ? `T ${2028 + 1}-${29 + 1}` : '', // Team option year 4 for 1st rounders
    isRookie: true,
    ...(ovr ? { ovr } : {}),
  };
}

/**
 * Full season advance: contracts + new schedule + reset state
 */
export function advanceSeason(salaryData, draftResults) {
  let sd = advanceContracts(salaryData);

  // Add drafted players to their teams
  if (draftResults) {
    for (const pick of draftResults) {
      if (!pick.team || !pick.prospect || !sd[pick.team]) continue;
      const contract = buildRookieContract(pick.prospect.name, pick.pick, pick.prospect.ovr);
      sd[pick.team].players.push(contract);
      sd[pick.team].payroll = sd[pick.team].players.reduce((s, p) => s + (p.salary2526 || 0), 0);
    }
  }

  return {
    salaryData: sd,
    seasonState: {
      schedule: generateSchedule(),
      playoffs: null,
      complete: false,
    },
    seasonPhase: 'regular',
  };
}
